import { createSlug } from './urlHelpers';

/**
 * Breadcrumb-style listing URLs.
 *
 *   /l/:category/:make/:model/:slug/:id
 *   /l/hjul/audi/a3/vinterdack-audi-a3-195-65r15/691f2cda-8510-4098-85ba-2cc9383cc28a
 *
 * Every segment comes from data the listing already stores:
 *   - category: publicData.adType (or the listing type on older listings)
 *   - make:     publicData.categoryLevel1
 *   - model:    publicData.categoryLevel2
 *   - slug:     the listing title, slugified and shortened
 *   - id:       the listing UUID, which is still the only lookup key
 *
 * A listing without a car make and model cannot form this path, so it keeps
 * the legacy /l/:slug/:id URL. See `listingPageLinkProps` at the bottom.
 */

// The Swedish words the public SEO landing pages already use
// (/dack/:make/:model and /falgar/:make/:model), so the whole site uses one
// vocabulary. `adType` is the filter field a buyer searches on.
const AD_TYPE_SEGMENTS = {
  wheel: 'hjul',
  tire: 'dack',
  rim: 'falgar',
};

// Listings created before `adType` existed only carry the listing type, which
// has a separate id for the private and the business variant.
const LISTING_TYPE_SEGMENTS = {
  wheel: 'hjul',
  'business-wheel': 'hjul',
  tire: 'dack',
  businesstire: 'dack',
  rim: 'falgar',
  businesswheel: 'falgar',
};

// Used when the product kind is unknown. "annons" is Swedish for listing.
export const DEFAULT_CATEGORY_SEGMENT = 'annons';

// Google shows roughly 60-70 characters of a URL in a search result. The
// product kind, make and model already have their own segments, so the title
// segment does not need to be longer than this.
export const TITLE_SLUG_MAX_LENGTH = 60;

/**
 * Slugify a listing title and shorten it to at most `maxLength` characters.
 *
 * The cut happens on a word boundary so the URL never ends in half a word.
 * A single word longer than the limit has no boundary to cut on, so it is
 * cut hard rather than dropped.
 *
 * @param {String} title listing title
 * @param {Number} maxLength longest slug we want to produce
 * @return {String} slug, never empty ('no-slug' when there is no title)
 */
export const createListingTitleSlug = (title, maxLength = TITLE_SLUG_MAX_LENGTH) => {
  const slug = createSlug(title || '');
  if (slug.length <= maxLength) {
    return slug;
  }

  const cut = slug.slice(0, maxLength);
  const lastWordBoundary = cut.lastIndexOf('-');
  const shortened = lastWordBoundary > 0 ? cut.slice(0, lastWordBoundary) : cut;
  return shortened.replace(/-+$/, '');
};

/**
 * Path params for the breadcrumb listing URL.
 *
 * @param {Object} listing listing entity
 * @return {Object|null} params for the 'ListingPage' route, or null when the
 * listing has no car make/model and therefore cannot use that route.
 */
export const listingSeoPathParams = listing => {
  const id = listing?.id?.uuid;
  const { title, publicData } = listing?.attributes || {};
  const make = publicData?.categoryLevel1;
  const model = publicData?.categoryLevel2;

  if (!id || !make || !model) {
    return null;
  }

  const category =
    AD_TYPE_SEGMENTS[publicData?.adType] ||
    LISTING_TYPE_SEGMENTS[publicData?.listingType] ||
    DEFAULT_CATEGORY_SEGMENT;

  // The stored category ids are not consistently lower case (e.g. make "audi"
  // but model "A3"). URL paths are case sensitive, so two spellings of the same
  // listing would be two URLs to a search engine. Slugify both segments to keep
  // one spelling.
  return {
    category,
    make: createSlug(make),
    model: createSlug(model),
    slug: createListingTitleSlug(title),
    id,
  };
};

/**
 * Route name and path params for linking to a listing page. Use this instead
 * of hardcoding name="ListingPage", so listings that cannot form a breadcrumb
 * URL still get a working link.
 *
 * @param {Object} listing listing entity
 * @param {Object} fallbackParams id/slug to fall back on when the listing
 * itself is not loaded yet, e.g. the params of the current route.
 * @return {Object} { name, params } for NamedLink / NamedRedirect /
 * createResourceLocatorString
 */
export const listingPageLinkProps = (listing, fallbackParams = {}) => {
  const seoParams = listingSeoPathParams(listing);
  if (seoParams) {
    return { name: 'ListingPage', params: seoParams };
  }

  const title = listing?.attributes?.title;
  return {
    name: 'ListingPageLegacy',
    params: {
      id: listing?.id?.uuid || fallbackParams.id,
      slug: title ? createListingTitleSlug(title) : fallbackParams.slug || 'no-slug',
    },
  };
};
